import React, { useMemo, useRef, useEffect } from "react";
import dateFormat, { i18n } from "dateformat";
import { useAppDispatch } from "@redux/store";
import { setDate } from "@redux/rent/slice";
import { setRentDateToStorage } from "@utils/index";
import { moscowNow } from "@utils/date/moscowNow";
import { dayNamesForRent } from "@resources/locale";
import styles from "./WeekDaysMobile.module.scss";

i18n.dayNames = dayNamesForRent;

interface WeekDaysMobileProps {
  date: string;
}

export const WeekDaysMobile: React.FC<WeekDaysMobileProps> = ({ date }) => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedDayRef = useRef<HTMLButtonElement>(null);

  const weekDays = useMemo(() => {
    if (!date) {
      return [];
    }

    const parsedDate = Date.parse(date);
    if (isNaN(parsedDate)) {
      return [];
    }

    const currentDate = new Date(parsedDate);
    if (isNaN(currentDate.getTime())) {
      return [];
    }
    
    const startDate = new Date(currentDate);
    startDate.setDate(currentDate.getDate() - 3);
    
    if (isNaN(startDate.getTime())) {
      return [];
    }
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      
      if (isNaN(day.getTime())) {
        continue;
      }

      const dayDate = new Date(day).setHours(0, 0, 0, 0);
      const nowDate = moscowNow().setHours(0, 0, 0, 0);
      const disableDate = dayDate < nowDate;

      days.push({
        dateString: dateFormat(day, "isoDate"),
        dayName: dateFormat(day, "dddd"),
        formattedDate: dateFormat(day, "dd.mm.yyyy"),
        textDateName: dateFormat(day, "ddd"),
        textDate: dateFormat(day, "dd.mm.yyyy"),
        dayNumber: dateFormat(day, "dd"),
        disabled: disableDate,
      });
    }
    
    return days;
  }, [date]);

  useEffect(() => {
    if (selectedDayRef.current && containerRef.current) {
      const container = containerRef.current;
      const selectedButton = selectedDayRef.current;
      
      const containerWidth = container.offsetWidth;
      const buttonLeft = selectedButton.offsetLeft;
      const buttonWidth = selectedButton.offsetWidth;
      const buttonCenter = buttonLeft + buttonWidth / 2;
      const scrollLeft = buttonCenter - containerWidth / 2;
      
      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [date, weekDays]);

  const handleDayClick = (dayDate: string) => {
    setRentDateToStorage(dayDate);
    dispatch(setDate(dayDate));
  };

  const isSelected = (dayDate: string) => {
    return dayDate === date;
  };

  if (!weekDays || weekDays.length === 0) {
    return null;
  }

  return (
    <div className={styles.weekDays} ref={containerRef}>
      {weekDays.map((day) => (
        <button
          key={day.dateString}
          ref={isSelected(day.dateString) ? selectedDayRef : null}
          className={`${styles.weekDay} ${day.disabled ? styles.weekDay_disabled : ""}`}
          onClick={() => handleDayClick(day.dateString)}
          disabled={day.disabled}
        >
          <span className={styles.weekDay_textDateName}>
            {day.textDateName}
          </span>
          <span className={`${styles.weekDay_number} ${isSelected(day.dateString) ? styles.weekDay_number_selected : ""}`}>
            {day.dayNumber}
          </span>
        </button>
      ))}
    </div>
  );
};
